import type { Stage } from '../api/types'
import type { StageHistoryEntry } from '../api/types'
import {
  STAGE_LABELS,
  STAGE_VARIANT,
  anomalyTypeLabel,
  extractionMarkerLabel,
  isExtractionMarker,
} from '../utils/stage-meta'
import { Chip } from '@/components/ui'

/**
 * Vertical timeline of an intake's stage_history.
 *
 * Three entry shapes share this JSONB column:
 *   1. Real commercial Stage transition (e.g. "needs_review")
 *   2. Synthetic `extraction:<state>` marker emitted by the worker
 *      (e.g. "extraction:running")
 *   3. CARLA anomaly (Sprint 33): {kind:"anomaly", anomaly_type,
 *      details, line_no?, actor:"carla"} — stage is null here.
 *
 * Each renders with a different chip variant so operators see at a
 * glance whether the event was operator-driven, system-emitted, or
 * a CARLA-flagged anomaly.
 */
export function StageHistoryTimeline({ entries }: { entries: StageHistoryEntry[] }) {
  if (!entries || entries.length === 0) {
    return (
      <p className="m-0 text-sm text-tertiary">
        No stage transitions recorded yet.
      </p>
    )
  }

  // Newest first — the JSONB array is appended in chronological order
  // server-side; reverse for display so the latest event reads first.
  const display = [...entries].reverse()

  return (
    <ol className="flex flex-col gap-2">
      {display.map((entry, i) => {
        // Anomaly entries (kind="anomaly") have no `stage`. Render with
        // a warning chip + anomaly_type label + details as the note.
        if (entry.kind === 'anomaly') {
          return (
            <li
              key={`${entry.at}-anomaly-${i}`}
              className="flex items-start gap-3 border-l border-warning pl-3"
            >
              <Chip size="xs" variant="warning" dot>
                {anomalyTypeLabel(entry.anomaly_type)}
              </Chip>
              <div className="min-w-0 flex-1">
                <div className="text-role-metadata">
                  {formatTimestamp(entry.at)}
                  {entry.actor ? ` · ${entry.actor}` : null}
                  {entry.line_no != null ? ` · line ${entry.line_no}` : null}
                </div>
                {entry.details ? (
                  <div className="mt-0.5 text-xs text-secondary">
                    {entry.details}
                  </div>
                ) : null}
              </div>
            </li>
          )
        }

        const stage = entry.stage // string | null
        const isExtraction = isExtractionMarker(stage)
        const label = isExtraction && stage
          ? extractionMarkerLabel(stage)
          : stage
            ? (STAGE_LABELS[stage as Stage] ?? stage)
            : '—'
        const variant = isExtraction
          ? 'neutral'
          : stage
            ? (STAGE_VARIANT[stage as Stage] ?? 'neutral')
            : 'neutral'

        return (
          <li
            key={`${entry.at}-${stage ?? 'unknown'}-${i}`}
            className="flex items-start gap-3 border-l border-border-subtle pl-3"
          >
            <Chip size="xs" variant={variant} dot>
              {label}
            </Chip>
            <div className="min-w-0 flex-1">
              <div className="text-role-metadata">
                {formatTimestamp(entry.at)}
                {entry.actor ? ` · ${entry.actor}` : null}
              </div>
              {entry.note ? (
                <div className="mt-0.5 text-xs text-secondary">{entry.note}</div>
              ) : null}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  } catch {
    return iso
  }
}
